import { createPortal } from "react-dom"
import { AppWindow, Loader2, WifiOff } from "lucide-react"

import { useFullscreenHost } from "@vc/viewmodels/screen/useFullscreenHost"
import { breakoutStore } from "@vc/viewmodels/breakout/useBreakoutRooms"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useMeetingBroadcastingStore } from "../store/meeting-broadcasting.store"
import type { ReconnectReason } from "../services/reconnect-banner"

/** Title and line under it, per reason. Keys live under `meetingScreen`. */
const COPY: Record<ReconnectReason, { title: string; detail: string }> = {
  offline: { title: "meetingScreen.reconnectOfflineTitle", detail: "meetingScreen.reconnectOfflineDetail" },
  signaling: { title: "meetingScreen.reconnectingTitle", detail: "meetingScreen.reconnectingSignalingDetail" },
  media: { title: "meetingScreen.reconnectingTitle", detail: "meetingScreen.reconnectingMediaDetail" },
  "media-failed": { title: "meetingScreen.reconnectMediaFailedTitle", detail: "meetingScreen.reconnectMediaFailedDetail" },
  elsewhere: { title: "meetingScreen.reconnectElsewhereTitle", detail: "meetingScreen.reconnectElsewhereDetail" },
}

/** Top-centre slots already taken by the live-stream pill and the room name badge. */
const SLOTS = ["top-4", "top-16", "top-28"] as const

/**
 * "Reconnecting…", over the meeting, while it is not fully connected.
 *
 * Not a toast: those cap at three and evict the oldest, are cleared by "Clear
 * all" and muted by the Notifications switch — and this has to stay up for as
 * long as the outage does. Not a modal either: the meeting keeps playing what
 * it still has, and nothing here needs an answer.
 *
 * The live region is always mounted so a screen reader announces the message
 * when it appears, which an element inserted together with its text does not
 * reliably get.
 */
export function ReconnectingBanner({ reason }: { reason: ReconnectReason | null }) {
  const t = useTranslation()
  const host = useFullscreenHost()
  // The pills that already hold the top centre: the live-stream indicator, and
  // the room name badge (shown while breakout rooms run, pushed below the
  // live pill when both are up). Step below whichever are there.
  const live = useMeetingBroadcastingStore((s) => s.isLive)
  const breakoutActive = breakoutStore((s) => s.breakoutActive)
  const slot = SLOTS[(live ? 1 : 0) + (breakoutActive ? 1 : 0)]

  const copy = reason ? COPY[reason] : null

  const region = (
    <div role="status" aria-live="polite">
      {copy && (
        <div
          className={cn(
            // Centred by inset + auto margins, not left-1/2 + translate: that
            // capped the pill at half the viewport, so on a phone it wrapped
            // into a tall blob. rounded-2xl, not -full, for when it does wrap.
            "fixed inset-x-4 mx-auto w-fit z-[9999] flex items-center gap-3 px-4 py-2",
            "rounded-2xl shadow-lg pointer-events-none",
            // amber-800: white on amber-600 failed WCAG AA for both lines.
            "bg-amber-800/95 backdrop-blur-sm ring-1 ring-white/15 text-white",
            slot,
          )}
        >
          {reason === "offline" ? (
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : reason === "elsewhere" ? (
            // Nothing is being retried, so nothing spins.
            <AppWindow className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">{t(copy.title)}</p>
            <p className="text-xs text-white/85 leading-snug">{t(copy.detail)}</p>
          </div>
        </div>
      )}
    </div>
  )

  // A shared screen or a maximised tile holds the whole fullscreen element;
  // anything left in the normal tree is painted behind it and never seen.
  //
  // Except a bare <video>: it renders no children, so a portal into one is
  // neither painted nor announced. Nothing can be drawn over a fullscreened
  // <video> at all — the toolbar would have to fullscreen a wrapper — so stay in
  // the page, where screen readers at least still hear it.
  if (!host || host === document.body || host.nodeName === "VIDEO") return region
  return createPortal(region, host)
}
